import { useIntl } from "react-intl";

import { ButtonBase } from "@/components/ButtonBase/ButtonBase";
import { Image } from "@/components/Image/Image";
import type { Trip } from "@/features/trips/types/types";
import { formatCurrency } from "@/utils/currency";

type Props = {
  trip: Trip;
  onClick: () => void;
};

export const TripCarouselCard = ({ trip, onClick }: Props) => {
  const intl = useIntl();

  const imageUrl = trip.imageUrls[0];

  return (
    <ButtonBase
      onClick={onClick}
      className="flex w-full flex-col items-start justify-start overflow-clip rounded-2xl border border-stroke-neutral-soft bg-white text-start"
    >
      <Image
        src={imageUrl}
        alt={trip.name}
        className="full-w aspect-17/10 shrink-0 object-cover object-center"
      />

      <div className="flex grow flex-col p-3.5 text-text-primary">
        <div className="mb-5 line-clamp-2 grow text-lg font-semibold">
          {trip.name}
        </div>

        {trip.price !== trip.discountedPrice ? (
          <div className="text-sm font-medium text-semantic-error-strong line-through">
            {formatCurrency(trip.price)}
          </div>
        ) : null}
        <div className="text-base font-bold">
          {intl.formatMessage(
            {
              defaultMessage: "From {price}",
              description:
                "Price prefix label indicating a starting / from price",
            },
            { price: formatCurrency(trip.discountedPrice) },
          )}
        </div>
        <div className="text-xs font-medium">
          {intl.formatMessage({
            defaultMessage: "per person",
            description:
              "Price qualifier indicating the displayed price is per person",
          })}{" "}
          <span className="text-[#D9D9D9]">&bull;</span>{" "}
          {intl.formatMessage(
            {
              defaultMessage:
                "{nights} {nights, plural, one {night} other {nights}} / {days} {days, plural, one {day} other {days}}",
              description: "Trip duration label (e.g. '7 nights')",
            },
            { nights: trip.durationInDays - 1, days: trip.durationInDays },
          )}
        </div>
      </div>
    </ButtonBase>
  );
};
